/**
 * API Client for Agent Portal.
 * Uses Keycloak OIDC Bearer tokens for authentication (ADR-0009).
 */
import { getConfig } from "../config";
import { getUserManager } from "../auth/oidcConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export class ApiClient {
  private customBaseUrl?: string;

  constructor(baseUrl?: string) {
    this.customBaseUrl = baseUrl;
  }

  private getBaseUrl(): string {
    if (this.customBaseUrl) {
      return this.customBaseUrl;
    }
    return getConfig().apiUrl;
  }

  private buildUrl(endpoint: string): URL {
    const origin =
      typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null"
        ? window.location.origin
        : "http://localhost";
    return new URL(`${this.getBaseUrl()}${endpoint}`, origin);
  }

  /**
   * Get headers with Bearer token from OIDC user session.
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    try {
      const mgr = getUserManager();
      const user = await mgr.getUser();
      if (user?.access_token) {
        headers["Authorization"] = `Bearer ${user.access_token}`;
      }
    } catch {
      // No OIDC user — request will proceed without auth header
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid — trigger re-auth
        localStorage.removeItem("agentPortalAgencyName");
        sessionStorage.removeItem("agentPortalSession");
        const mgr = getUserManager();
        await mgr.removeUser();
        window.location.replace("/login");
        throw new Error("Unauthorised");
      }

      let errorMessage = "An error occurred";
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.Message || errorData.title || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const url = this.buildUrl(endpoint);
    if (params) {
      Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: await this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async getText(endpoint: string): Promise<string> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "GET",
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("agentPortalAgencyName");
        sessionStorage.removeItem("agentPortalSession");
        const mgr = getUserManager();
        await mgr.removeUser();
        window.location.replace("/login");
        throw new Error("Unauthorised");
      }
      throw new Error(response.statusText || "Failed to fetch text content");
    }

    return response.text();
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "POST",
      headers: await this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "PUT",
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "PATCH",
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "DELETE",
      headers: await this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const mgr = getUserManager();
    const user = await mgr.getUser();
    const headers: HeadersInit = {};
    if (user?.access_token) {
      headers["Authorization"] = `Bearer ${user.access_token}`;
    }

    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "POST",
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: "GET",
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const apiClient = new ApiClient();
